<template>
  <div class="cq-page py-3">
    <header class="cq-toolbar">
      <div class="cq-toolbar__title">
        <span class="cq-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Call Queues</h1>
          <p>Manage tele staff per region and trigger the queue-generation pipelines</p>
        </div>
      </div>
      <div class="cq-toolbar__actions">
        <span v-if="lastUpdatedAt && !isLoading" class="cq-updated">
          Updated {{ lastUpdatedLabel }}
        </span>
        <CButton size="sm" class="refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadData">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="cq-alert">{{ errorMessage }}</CAlert>

    <!-- Org tabs -->
    <div class="cq-tabs">
      <button
        v-for="org in orgs"
        :key="org.id"
        type="button"
        class="cq-tab"
        :class="{ 'is-active': activeOrgId === org.id, 'is-disabled': !org.enabled }"
        :disabled="!org.enabled"
        :title="org.enabled ? '' : 'Coming soon'"
        @click="org.enabled && (activeOrgId = org.id)"
      >
        <span class="cq-tab__name">{{ org.name }}</span>
        <span v-if="!org.enabled" class="cq-tab__badge">soon</span>
      </button>
    </div>

    <!-- Pipelines panel -->
    <section class="cq-pipelines">
      <header class="cq-pipelines__head">
        <div class="cq-pipelines__title">
          <h2>Pipelines</h2>
          <p>Each tile shows when the cron last wrote to the queue table and how many rows it produced. The dot turns green within 24h, amber after a day, red after three days.</p>
        </div>
        <button
          type="button"
          class="cq-runall"
          :disabled="runAllRunning || !!runningPipeline || !pipelines.length"
          :title="pipelines.length ? 'Regenerate every pipeline in sequence.' : 'No pipelines configured for this organisation.'"
          @click="runAllPipelines"
        >
          <CSpinner v-if="runAllRunning" size="sm" class="cq-runall__spinner" />
          <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="cq-runall__icon" aria-hidden="true">
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
          <span>{{ runAllRunning ? `Running ${runAllProgress.current}/${runAllProgress.total}…` : 'Run all' }}</span>
        </button>
      </header>
      <div class="cq-pipelines__grid">
        <div
          v-for="p in pipelines"
          :key="p.key"
          class="cq-pipeline"
          :class="[`cq-pipeline--${statusClassFor(p.key)}`, { 'is-running': runningPipeline === p.key }]"
          :title="statusLabelFor(p.key)"
        >
          <div class="cq-pipeline__name">{{ p.label }}</div>
          <div class="cq-pipeline__metric">
            <span class="cq-pipeline__count">{{ formatNumber(pipelineRowCount(p.key)) }}</span>
            <span class="cq-pipeline__unit">rows</span>
          </div>
          <div v-if="pipelineLastRun(p.key)" class="cq-pipeline__time">
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cq-pipeline__time-icon" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>{{ formatTime(pipelineLastRun(p.key)) }}</span>
          </div>
          <div v-else class="cq-pipeline__time cq-pipeline__time--never">
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cq-pipeline__time-icon" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Never run</span>
          </div>
          <button
            type="button"
            class="cq-pipeline__btn"
            :disabled="!!runningPipeline || runAllRunning"
            @click="runPipeline(p.key)"
          >
            <CSpinner v-if="runningPipeline === p.key" size="sm" class="cq-pipeline__spinner" />
            <svg v-else viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="cq-pipeline__btn-icon" aria-hidden="true">
              <polygon points="6 4 20 12 6 20 6 4"/>
            </svg>
            <span>{{ runningPipeline === p.key ? 'Running…' : 'Run now' }}</span>
          </button>
        </div>
      </div>
    </section>

    <!-- Day thresholds (admin-tunable) -->
    <section v-if="daySettings.length || daySettingsLoading" class="cq-days">
      <header class="cq-days__head">
        <h2>Cron day thresholds</h2>
        <p>Tune how old a call or order must be before it recycles into a queue. Changes apply on the next cron run; existing rows already in queue tables are not affected.</p>
      </header>

      <div v-if="daySettingsLoading" class="cq-days__loading">Loading…</div>

      <ul v-else class="cq-day-list">
        <li v-for="s in daySettings" :key="s.key" class="cq-day-row" :class="{ 'is-dirty': isDayDirty(s.key) }">
          <div class="cq-day-row__body">
            <div class="cq-day-row__title">
              <span class="cq-day-row__label">{{ s.label }}</span>
              <span v-if="s.is_override && !isDayDirty(s.key)" class="cq-day-row__chip">custom</span>
              <span v-if="isDayDirty(s.key)" class="cq-day-row__chip cq-day-row__chip--dirty">unsaved</span>
            </div>
            <p class="cq-day-row__desc">{{ s.description }}</p>
            <p class="cq-day-row__bounds">Allowed: {{ s.min }}–{{ s.max }} days · Default: {{ s.default }}</p>
          </div>
          <div class="cq-day-row__field">
            <div class="cq-day-row__inputwrap">
              <input
                type="number"
                class="cq-day-row__input"
                :min="s.min"
                :max="s.max"
                :value="daySettingValue(s.key)"
                @input="setDaySetting(s.key, $event.target.value)"
              />
              <span class="cq-day-row__suffix">days</span>
            </div>
            <button
              v-if="canResetDaySetting(s)"
              type="button"
              class="cq-day-row__reset"
              :disabled="daySettingsSaving"
              @click="resetDaySetting(s)"
            >
              Reset to {{ s.default }}
            </button>
          </div>
        </li>
      </ul>

      <div v-if="!daySettingsLoading" class="cq-days__actions">
        <span v-if="hasDirtyDays" class="cq-days__hint">You have unsaved changes.</span>
        <button
          type="button"
          class="cq-days__save"
          :disabled="!hasDirtyDays || daySettingsSaving"
          @click="saveDaySettings"
        >
          <CSpinner v-if="daySettingsSaving" size="sm" class="cq-days__spinner" />
          <span>{{ daySettingsSaving ? 'Saving…' : 'Save day thresholds' }}</span>
        </button>
      </div>
    </section>

    <!-- Bulk import fresh leads -->
    <section v-if="freshLeadsSupported" class="fli-card">
      <header class="fli-card__head">
        <div class="fli-card__title">
          <h2>Bulk import fresh leads</h2>
          <p>Upload a farmer-customer dataset (.xlsx, .xls, or .csv). The importer auto-detects column headers; you can review the mapping before committing. Duplicates by phone are skipped. Leads land in the shared MAP + MVP pool — upload from either org and it'll be distributed to both teams when the Fresh leads cron runs.</p>
        </div>
      </header>
      <div class="fli-card__body">
        <label
          class="fli-dropzone"
          :class="{ 'is-dragover': freshLeads.isDragOver, 'is-filled': !!freshLeads.file, 'is-disabled': freshLeads.uploading }"
          @dragenter.prevent="onFreshLeadDragEnter"
          @dragover.prevent="onFreshLeadDragOver"
          @dragleave.prevent="onFreshLeadDragLeave"
          @drop.prevent="onFreshLeadDrop"
        >
          <input type="file" accept=".xlsx,.xls,.csv" :disabled="freshLeads.uploading" @change="onFreshLeadFileChange" />
          <div class="fli-dropzone__inner">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="fli-dropzone__icon" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <div v-if="!freshLeads.file" class="fli-dropzone__text">
              <strong>Drag & drop</strong> a file here, or <span class="fli-dropzone__link">browse</span>
              <div class="fli-dropzone__hint">.xlsx, .xls, or .csv · up to 50 000 rows</div>
            </div>
            <div v-else class="fli-dropzone__text">
              <strong class="fli-dropzone__filename" :title="freshLeads.file.name">{{ freshLeads.file.name }}</strong>
              <div class="fli-dropzone__hint">{{ formatFileSize(freshLeads.file.size) }} · <span class="fli-dropzone__link">click to change</span></div>
            </div>
          </div>
        </label>

        <div class="fli-controls">
          <button
            type="button"
            class="fli-btn fli-btn--primary"
            :disabled="!canPreviewFreshLeads"
            @click="previewFreshLeads"
          >
            <CSpinner v-if="freshLeads.uploading" size="sm" class="fli-btn__spinner" />
            <span>{{ freshLeads.uploading ? 'Parsing…' : 'Preview & import' }}</span>
          </button>
        </div>
        <div v-if="freshLeads.error" class="fli-alert" role="alert">{{ freshLeads.error }}</div>
        <div v-if="freshLeads.lastSummary" class="fli-summary">{{ freshLeads.lastSummary }}</div>
      </div>
    </section>

    <!-- Region cards -->
    <div v-if="!regions.length && !isLoading" class="cq-empty">
      <div class="cq-empty__title">No regions configured for this organisation</div>
      <div class="cq-empty__hint">MAP (org 1) is the only org wired up in this slice. MVP and MDI will follow.</div>
    </div>

    <section v-for="region in regions" :key="region.code" class="cq-region">
      <header class="cq-region__head">
        <div class="cq-region__title">
          <span class="cq-region__marker" :style="{ background: regionColor(region.code) }" aria-hidden="true"></span>
          <h2>{{ region.department }}</h2>
          <span class="cq-region__chip">{{ region.code }}</span>
        </div>
        <div class="cq-region__counts">
          <span class="cq-region__count">{{ region.active_count }} active</span>
          <span class="cq-region__dot">·</span>
          <span class="cq-region__count cq-region__count--muted">{{ region.resigned_count }} resigned</span>
        </div>
      </header>

      <!-- Active roster -->
      <div class="cq-roster">
        <div class="cq-roster__head">
          <h3>Active members</h3>
          <button type="button" class="cq-add-btn" @click="openPicker(region.code, 'active')">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Add staff</span>
          </button>
        </div>
        <div v-if="!region.active.length" class="cq-roster__empty">
          No active members. Add one to start receiving call-queue assignments.
        </div>
        <ul v-else class="cq-member-list">
          <li v-for="user in region.active" :key="user.id" class="cq-member">
            <span class="cq-member__avatar" :style="avatarStyle(user)">
              <img v-if="user.image" :src="user.image" :alt="user.name" @error="onAvatarError($event, user)" />
              <span v-else>{{ initials(user.name) }}</span>
            </span>
            <div class="cq-member__body">
              <span class="cq-member__name">{{ user.name }}</span>
              <span class="cq-member__email">{{ user.email }}</span>
            </div>
            <div class="cq-member__actions">
              <button
                type="button"
                class="cq-icon-btn cq-icon-btn--warn"
                :disabled="busyUserId === user.id"
                title="Mark as resigned"
                aria-label="Mark as resigned"
                @click="markResigned(region, user)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
              <button
                type="button"
                class="cq-icon-btn cq-icon-btn--danger"
                :disabled="busyUserId === user.id"
                title="Remove from this region"
                aria-label="Remove from this region"
                @click="removeMember(region, user, 'active')"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </li>
        </ul>
      </div>

      <!-- Resigned pool -->
      <details class="cq-roster cq-roster--resigned">
        <summary class="cq-roster__summary">
          <h3>Resigned recycle pool</h3>
          <span class="cq-roster__hint">Their old calls and orders feed the active members in this region.</span>
        </summary>
        <div class="cq-roster__body">
          <div class="cq-roster__head cq-roster__head--inline">
            <button type="button" class="cq-add-btn cq-add-btn--small" @click="openPicker(region.code, 'resigned')">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <span>Add resigned user</span>
            </button>
          </div>
          <div v-if="!region.resigned.length" class="cq-roster__empty">
            No resigned users tracked for this region.
          </div>
          <ul v-else class="cq-member-list cq-member-list--resigned">
            <li v-for="user in region.resigned" :key="user.id || user.cqr_id" class="cq-member">
              <span class="cq-member__avatar" :style="avatarStyle(user)">
                <img v-if="user.image" :src="user.image" :alt="user.name" @error="onAvatarError($event, user)" />
                <span v-else>{{ initials(user.name) }}</span>
              </span>
              <div class="cq-member__body">
                <span class="cq-member__name">{{ user.name || `User #${user.id}` }}</span>
                <span class="cq-member__email">{{ user.email }}</span>
                <span v-if="user.notes" class="cq-member__note">“{{ user.notes }}”</span>
              </div>
              <div class="cq-member__actions">
                <button
                  type="button"
                  class="cq-icon-btn cq-icon-btn--success"
                  :disabled="busyUserId === user.id"
                  title="Restore as active"
                  aria-label="Restore as active"
                  @click="restoreActive(region, user)"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="9 14 4 9 9 4"/>
                    <path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                  </svg>
                </button>
                <button
                  type="button"
                  class="cq-icon-btn cq-icon-btn--danger"
                  :disabled="busyUserId === user.id"
                  title="Remove from this region"
                  aria-label="Remove from this region"
                  @click="removeMember(region, user, 'resigned')"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </details>
    </section>

    <!-- Fresh-leads preview / confirm modal -->
    <CModal
      :show.sync="freshLeads.preview.show"
      size="lg"
      :centered="true"
      title="Review column mapping"
      class="fli-modal"
      :close-on-backdrop="!freshLeads.confirming"
    >
      <div class="fli-preview">
        <div class="fli-preview__head">
          <div>
            <div class="fli-preview__file">{{ freshLeads.preview.fileName }}</div>
            <div class="fli-preview__meta">{{ freshLeads.preview.totalRows }} data rows detected</div>
          </div>
        </div>

        <div v-if="freshLeads.preview.confirmError" class="fli-alert" role="alert">{{ freshLeads.preview.confirmError }}</div>

        <div class="fli-mapping">
          <div class="fli-mapping__title">Column mapping</div>
          <div class="fli-mapping__grid">
            <div v-for="field in freshLeads.preview.canonicalFields" :key="field" class="fli-mapping__row">
              <label :for="`fli-map-${field}`" class="fli-mapping__label">
                {{ prettyFieldLabel(field) }}
                <span v-if="isRequiredField(field)" class="fli-mapping__req">*</span>
              </label>
              <select
                :id="`fli-map-${field}`"
                v-model="freshLeads.preview.editableMap[field]"
                class="fli-mapping__select"
              >
                <option :value="null">— Not mapped —</option>
                <option v-for="(header, idx) in freshLeads.preview.headers" :key="idx" :value="idx">
                  {{ header || `Column ${idx + 1}` }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="fli-sample">
          <div class="fli-sample__title">Sample rows (first {{ freshLeads.preview.sampleRows.length }})</div>
          <div class="fli-sample__scroll">
            <table class="fli-sample__table">
              <thead>
                <tr>
                  <th v-for="(header, idx) in freshLeads.preview.headers" :key="idx">
                    {{ header || `Column ${idx + 1}` }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in freshLeads.preview.sampleRows" :key="ri">
                  <td v-for="colIdx in previewColumnIndices" :key="colIdx">{{ row[colIdx] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <template #footer>
        <button type="button" class="fli-btn fli-btn--ghost" :disabled="freshLeads.confirming" @click="closeFreshLeadsPreview">Cancel</button>
        <button type="button" class="fli-btn fli-btn--primary" :disabled="!canConfirmFreshLeads" @click="confirmFreshLeads">
          <CSpinner v-if="freshLeads.confirming" size="sm" class="fli-btn__spinner" />
          <span>{{ freshLeads.confirming ? 'Importing…' : 'Confirm import' }}</span>
        </button>
      </template>
    </CModal>

    <!-- User picker modal -->
    <CModal :show.sync="picker.show" :title="pickerTitle" :centered="true" class="cq-modal" :close-on-backdrop="!picker.submitting">
      <div class="cq-picker">
        <div v-if="picker.error" class="cq-picker__alert" role="alert">
          <span class="cq-picker__alert-text">{{ picker.error }}</span>
          <button type="button" class="cq-picker__alert-close" aria-label="Dismiss error" @click="picker.error = ''">×</button>
        </div>
        <div class="cq-picker__searchwrap">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cq-picker__icon" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref="pickerInput"
            v-model="picker.query"
            type="text"
            class="cq-picker__input"
            placeholder="Search by name or email…"
            :disabled="picker.submitting"
          />
        </div>

        <div class="cq-picker__toolbar">
          <label class="cq-picker__selectall" :class="{ 'is-disabled': !filteredPickerResults.length || picker.submitting }">
            <input
              type="checkbox"
              :checked="allFilteredSelected"
              :disabled="!filteredPickerResults.length || picker.submitting"
              @change="toggleSelectAllVisible"
            />
            <span>Select all visible</span>
          </label>
          <span v-if="picker.selectedUsers.length" class="cq-picker__counter">
            {{ picker.selectedUsers.length }} selected
            <button
              type="button"
              class="cq-picker__clear"
              :disabled="picker.submitting"
              @click="clearSelection"
            >clear</button>
          </span>
        </div>

        <div v-if="picker.loading" class="cq-picker__state">
          <CSpinner size="sm" />
          <span>Searching…</span>
        </div>
        <div v-else-if="!picker.results.length" class="cq-picker__state cq-picker__state--empty">
          {{ picker.query ? 'No matches.' : 'Start typing to find a user.' }}
        </div>
        <div v-else-if="!filteredPickerResults.length" class="cq-picker__state cq-picker__state--empty">
          All matching users are already on this organisation's roster.
        </div>
        <ul v-else class="cq-picker__list">
          <li v-for="user in filteredPickerResults" :key="user.id">
            <label
              class="cq-picker__item"
              :class="{ 'is-selected': isUserSelected(user.id), 'is-disabled': picker.submitting }"
            >
              <input
                type="checkbox"
                class="cq-picker__check"
                :checked="isUserSelected(user.id)"
                :disabled="picker.submitting"
                @change="togglePickerUser(user)"
              />
              <span class="cq-member__avatar" :style="avatarStyle(user)">
                <img v-if="user.image" :src="user.image" :alt="user.name" @error="onAvatarError($event, user)" />
                <span v-else>{{ initials(user.name) }}</span>
              </span>
              <div class="cq-member__body">
                <span class="cq-member__name">{{ user.name }}</span>
                <span class="cq-member__email">{{ user.email }}</span>
              </div>
            </label>
          </li>
        </ul>
      </div>

      <template #footer>
        <button type="button" class="cancel-btn" :disabled="picker.submitting" @click="closePicker">Cancel</button>
        <button
          type="button"
          class="cq-picker__submit"
          :disabled="!canConfirmBulk"
          @click="confirmBulkPicks"
        >
          <CSpinner v-if="picker.submitting" size="sm" class="cq-picker__submit-spinner" />
          <span v-if="picker.submitting">Adding {{ picker.submitProgress.done }} / {{ picker.submitProgress.total }}…</span>
          <span v-else-if="picker.selectedUsers.length">Add {{ picker.selectedUsers.length }} {{ picker.selectedUsers.length === 1 ? 'user' : 'users' }}</span>
          <span v-else>Add users</span>
        </button>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'

const ORGS = [
  { id: 1, name: 'MAP · Manakkal Agro Pharma', enabled: true },
  { id: 2, name: 'MDI · Manakkal Drugs India', enabled: true },
  { id: 4, name: 'MVP · Manakkal Veterinary Pharma', enabled: true }
]

// Pipelines visible per-org. Keep keys aligned with
// CallQueueAdminController::ORG_PIPELINES on the backend — sending an
// unknown key to /api/call-queue/run/{key} returns 404. MDI's
// high_priority_followups stays live-queried (no table, no Run-now tile).
const PIPELINES_BY_ORG = {
  1: [
    { key: 'own_followups',        label: 'Own followups' },
    { key: 'hp_followups',         label: 'High-priority followups' },
    { key: 'na_common_pool',       label: 'Not-attended pool' },
    { key: 'additional_followups', label: 'Additional followups' },
    { key: 'own_feedbacks',        label: 'Own feedbacks' },
    { key: 'additional_feedbacks', label: 'Additional feedbacks' },
    { key: 'fresh_leads',          label: 'Fresh leads' },
    { key: 'returns',              label: 'Returns' },
    { key: 'mvp_leads_followups',  label: 'MVP leads (calls)' },
    { key: 'mvp_leads_feedback',   label: 'MVP leads (feedback)' },
    { key: 'bulk_priority',        label: 'Bulk priority' },
    { key: 'myth_breakers',        label: 'Myth breakers' },
    { key: 'repeated_attempts',    label: 'Repeated attempts' }
  ],
  2: [
    { key: 'own_followups',           label: 'Own followups' },
    { key: 'hp_followups',            label: 'High-priority followups' },
    { key: 'doctors_followups',       label: 'Doctor followups' },
    { key: 'priority_followups',      label: 'Priority followups' },
    { key: 'na_common_pool',          label: 'Not-attended pool' },
    { key: 'bulk_priority',           label: 'Bulk priority' },
    { key: 'myth_breakers',           label: 'Myth breakers' },
    { key: 'repeated_attempts',       label: 'Repeated attempts' },
    { key: 'map_leads_bulk_priority', label: 'MAP leads (bulk priority)' },
    { key: 'map_leads_followups',     label: 'MAP leads (calls)' },
    { key: 'map_leads_feedback',      label: 'MAP leads (feedback)' }
  ],
  4: [
    { key: 'own_followups',        label: 'Own followups' },
    { key: 'hp_followups',         label: 'High-priority followups' },
    { key: 'na_common_pool',       label: 'Not-attended pool' },
    { key: 'additional_followups', label: 'Additional followups' },
    { key: 'own_feedbacks',        label: 'Own feedbacks' },
    { key: 'additional_feedbacks', label: 'Additional feedbacks' },
    { key: 'fresh_leads',          label: 'Fresh leads' },
    { key: 'map_leads_followups',  label: 'MAP leads (calls)' },
    { key: 'map_leads_feedback',   label: 'MAP leads (feedback)' },
    { key: 'bulk_priority',        label: 'Bulk priority' },
    { key: 'myth_breakers',        label: 'Myth breakers' },
    { key: 'repeated_attempts',    label: 'Repeated attempts' }
  ]
}

const REGION_PALETTE = {
  KERALA:    'linear-gradient(135deg, #0e5b46, #16a07a)',
  KARNATAKA: 'linear-gradient(135deg, #b45309, #f59e0b)',
  TAMIL:     'linear-gradient(135deg, #1e3a8a, #3b82f6)'
}

const AVATAR_PALETTE = [
  ['#0e5b46', '#16a07a'],
  ['#1e3a8a', '#3b82f6'],
  ['#5b21b6', '#8b5cf6'],
  ['#b45309', '#f59e0b'],
  ['#115e59', '#14b8a6'],
  ['#7f1d1d', '#ef4444']
]

let pickerSearchTimer = null

export default {
  name: 'SettingsCallQueues',
  data () {
    return {
      orgs: ORGS,
      activeOrgId: 1,
      isLoading: false,
      errorMessage: '',
      regions: [],
      pipelineStatus: {},
      lastUpdatedAt: null,
      runningPipeline: null,
      runAllRunning: false,
      runAllProgress: { current: 0, total: 0 },
      busyUserId: null,
      daySettings: [],
      daySettingsDirty: {},
      daySettingsLoading: false,
      daySettingsSaving: false,
      picker: {
        show: false,
        regionCode: '',
        target: 'active',
        query: '',
        results: [],
        loading: false,
        submitting: false,
        error: '',
        selectedUsers: [],
        submitProgress: { done: 0, total: 0 }
      },
      freshLeads: {
        file: null,
        uploading: false,
        confirming: false,
        isDragOver: false,
        lastSummary: '',
        error: '',
        preview: {
          show: false,
          importId: null,
          fileName: '',
          totalRows: 0,
          headers: [],
          sampleRows: [],
          detectedMap: {},
          editableMap: {},
          canonicalFields: [],
          requiredFields: [],
          confirmError: ''
        }
      }
    }
  },
  computed: {
    lastUpdatedLabel () {
      if (!this.lastUpdatedAt) return ''
      const h = String(this.lastUpdatedAt.getHours()).padStart(2, '0')
      const m = String(this.lastUpdatedAt.getMinutes()).padStart(2, '0')
      return `${h}:${m}`
    },
    pickerTitle () {
      if (!this.picker.regionCode) return 'Add member'
      const verb = this.picker.target === 'resigned' ? 'to resigned pool' : 'to active roster'
      return `Add to ${this.picker.regionCode} · ${verb}`
    },
    hasDirtyDays () {
      return Object.keys(this.daySettingsDirty).some((k) => this.isDayDirty(k))
    },
    // Pipelines visible for the active org. Different orgs have different
    // pipeline sets (MAP has 8, MDI has 4) so the tile grid + Run-all loop
    // both read this — never the static const.
    pipelines () {
      return PIPELINES_BY_ORG[this.activeOrgId] || []
    },
    // Fresh-leads import is only wired for orgs that participate in
    // the shared pool. Keep in sync with
    // FreshLeadsImportController::SUPPORTED_ORGS.
    freshLeadsSupported () {
      return this.activeOrgId === 1 || this.activeOrgId === 4
    },
    canPreviewFreshLeads () {
      if (!this.freshLeads.file || this.freshLeads.uploading) return false
      return true
    },
    canConfirmFreshLeads () {
      if (this.freshLeads.confirming) return false
      const map = this.freshLeads.preview.editableMap || {}
      for (const f of this.freshLeads.preview.requiredFields) {
        if (map[f] === null || map[f] === undefined) return false
      }
      return true
    },
    // Stable column indices used by the sample-rows table so the v-for
    // can pull `row[colIdx]` without introducing an unused loop var.
    previewColumnIndices () {
      const len = (this.freshLeads.preview.headers || []).length
      return Array.from({ length: len }, (_, i) => i)
    },
    // User ids already in any region's active or resigned roster for the
    // current org — these get hidden from the picker so an admin can't try
    // to "add" someone who's already on the board.
    addedUserIds () {
      const set = new Set()
      this.regions.forEach((region) => {
        const active = region.active || []
        const resigned = region.resigned || []
        active.forEach((u) => { if (u && u.id != null) set.add(u.id) })
        resigned.forEach((u) => { if (u && u.id != null) set.add(u.id) })
      })
      return set
    },
    filteredPickerResults () {
      const added = this.addedUserIds
      return (this.picker.results || []).filter((u) => !added.has(u.id))
    },
    selectedUserIdSet () {
      return new Set((this.picker.selectedUsers || []).map((u) => u.id))
    },
    allFilteredSelected () {
      const filtered = this.filteredPickerResults
      if (!filtered.length) return false
      const sel = this.selectedUserIdSet
      return filtered.every((u) => sel.has(u.id))
    },
    canConfirmBulk () {
      return !this.picker.submitting && this.picker.selectedUsers.length > 0
    }
  },
  watch: {
    activeOrgId () {
      this.loadData()
    },
    'picker.query' () {
      clearTimeout(pickerSearchTimer)
      pickerSearchTimer = setTimeout(() => this.searchUsers(), 200)
    }
  },
  created () {
    this._loadedOnMount = true
    this._unloadGuard = (event) => {
      if (this.runAllRunning) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this._unloadGuard)
    this.loadData()
  },
  beforeDestroy () {
    if (this._unloadGuard) {
      window.removeEventListener('beforeunload', this._unloadGuard)
    }
  },
  // The page sits inside <keep-alive> in TheContainer, so created() only
  // fires once. activated() runs on every visit (including the first), so we
  // skip the duplicate load on initial mount and re-fetch on each revisit
  // — admins don't see stale roster / pipeline / day-setting data.
  activated () {
    if (this._loadedOnMount) {
      this._loadedOnMount = false
      return
    }
    this.loadData()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    loadData () {
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/call-queue/${this.activeOrgId}/rosters?token=${this.authToken()}`)
        .then((response) => {
          this.regions = Array.isArray(response.data?.regions) ? response.data.regions : []
          this.pipelineStatus = response.data?.pipelines || {}
          this.lastUpdatedAt = new Date()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load call-queue rosters.'
        })
        .finally(() => { this.isLoading = false })
      this.loadDaySettings()
    },
    loadDaySettings () {
      this.daySettingsLoading = true
      this.daySettingsDirty = {}
      // Clear before fetching so an org switch doesn't briefly flash the
      // previous org's values while the new request is in flight.
      this.daySettings = []
      axios.get(`${this.$apiAdress}/api/call-queue/${this.activeOrgId}/day-settings?token=${this.authToken()}`)
        .then((response) => {
          this.daySettings = Array.isArray(response.data?.settings) ? response.data.settings : []
        })
        .catch((error) => {
          // 404 = no day settings configured for this org (only MAP for now). Hide section silently.
          if (error?.response?.status === 404) {
            this.daySettings = []
          } else {
            console.log(error)
          }
        })
        .finally(() => { this.daySettingsLoading = false })
    },
    daySettingValue (key) {
      if (Object.prototype.hasOwnProperty.call(this.daySettingsDirty, key)) {
        return this.daySettingsDirty[key]
      }
      const s = this.daySettings.find((x) => x.key === key)
      return s ? s.days : ''
    },
    setDaySetting (key, value) {
      this.$set(this.daySettingsDirty, key, value)
    },
    isDayDirty (key) {
      if (!Object.prototype.hasOwnProperty.call(this.daySettingsDirty, key)) return false
      const s = this.daySettings.find((x) => x.key === key)
      if (!s) return true
      const dirty = this.daySettingsDirty[key]
      if (dirty === '' || dirty === null || dirty === undefined) return true
      return Number(dirty) !== Number(s.days)
    },
    canResetDaySetting (s) {
      // Show "Reset" when the saved value differs from the schema default,
      // OR when the user has typed something different from the saved value.
      return Number(this.daySettingValue(s.key)) !== Number(s.default)
    },
    resetDaySetting (s) {
      this.$set(this.daySettingsDirty, s.key, s.default)
    },
    saveDaySettings () {
      const values = {}
      this.daySettings.forEach((s) => {
        if (this.isDayDirty(s.key)) {
          values[s.key] = Number(this.daySettingsDirty[s.key])
        }
      })
      if (!Object.keys(values).length) return

      this.daySettingsSaving = true
      axios.post(
        `${this.$apiAdress}/api/call-queue/${this.activeOrgId}/day-settings?token=${this.authToken()}`,
        { values }
      )
        .then((response) => {
          this.$toast.success(response.data?.message || 'Day thresholds updated.')
          this.loadDaySettings()
        })
        .catch((error) => {
          console.log(error)
          const msg = error?.response?.data?.message || 'Save failed.'
          const errs = error?.response?.data?.errors
          if (errs && typeof errs === 'object') {
            this.$toast.error(`${msg} ${Object.values(errs).join(' ')}`)
          } else {
            this.$toast.error(msg)
          }
        })
        .finally(() => { this.daySettingsSaving = false })
    },
    runPipeline (key) {
      this.runningPipeline = key
      axios.post(
        `${this.$apiAdress}/api/call-queue/run/${key}?organisation_id=${this.activeOrgId}&token=${this.authToken()}`
      )
        .then((response) => {
          const msg = response.data?.result?.message || `${this.labelFor(key)} regenerated.`
          const rows = response.data?.result?.total_inserted ?? response.data?.result?.total_generated
          this.$toast.success(msg, {
            description: rows !== undefined ? `${rows} rows written` : ''
          })
          // Refresh status timestamps + counts.
          this.loadData()
        })
        .catch((error) => {
          console.log(error)
          const msg = error?.response?.data?.message || 'Pipeline run failed.'
          this.$toast.error(msg)
        })
        .finally(() => { this.runningPipeline = null })
    },
    async runAllPipelines () {
      if (this.runAllRunning || this.runningPipeline) return
      const pipelines = this.pipelines
      if (!pipelines.length) return
      this.runAllRunning = true
      this.runAllProgress = { current: 0, total: pipelines.length }
      let succeeded = 0
      const failed = []
      // Hard cap per pipeline so a hung backend can't lock the loop forever.
      // Heaviest pipeline (MAP additional followups, 26 users × ~5k pool)
      // tops out around 60s on prod hardware; 5 min is generous headroom.
      const PER_PIPELINE_TIMEOUT_MS = 5 * 60 * 1000
      for (let i = 0; i < pipelines.length; i += 1) {
        const p = pipelines[i]
        this.runAllProgress = { current: i + 1, total: pipelines.length }
        this.runningPipeline = p.key
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(
            `${this.$apiAdress}/api/call-queue/run/${p.key}?organisation_id=${this.activeOrgId}&token=${this.authToken()}`,
            null,
            { timeout: PER_PIPELINE_TIMEOUT_MS }
          )
          succeeded += 1
        } catch (error) {
          console.log(error)
          failed.push(p.label)
        }
      }
      this.runningPipeline = null
      this.runAllRunning = false
      this.runAllProgress = { current: 0, total: 0 }

      if (!failed.length) {
        this.$toast.success(`All ${succeeded} pipelines regenerated.`)
      } else if (succeeded > 0) {
        this.$toast.error(`${failed.length} pipeline${failed.length === 1 ? '' : 's'} failed: ${failed.join(', ')}. ${succeeded} succeeded.`)
      } else {
        this.$toast.error(`Run-all failed for every pipeline.`)
      }
      this.loadData()
    },
    openPicker (regionCode, target) {
      this.picker.show = true
      this.picker.regionCode = regionCode
      this.picker.target = target
      this.picker.query = ''
      this.picker.results = []
      this.picker.error = ''
      this.picker.selectedUsers = []
      this.picker.submitProgress = { done: 0, total: 0 }
      this.$nextTick(() => {
        this.searchUsers()
        this.$refs.pickerInput && this.$refs.pickerInput.focus()
      })
    },
    closePicker () {
      this.picker.show = false
      this.picker.regionCode = ''
      this.picker.results = []
      this.picker.selectedUsers = []
      this.picker.submitProgress = { done: 0, total: 0 }
    },
    searchUsers () {
      this.picker.loading = true
      const q = encodeURIComponent(this.picker.query || '')
      axios.get(`${this.$apiAdress}/api/call-queue/users?organisation_id=${this.activeOrgId}&q=${q}&token=${this.authToken()}`)
        .then((response) => {
          this.picker.results = Array.isArray(response.data?.users) ? response.data.users : []
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.picker.loading = false })
    },
    // ---------- Fresh-leads bulk import ----------
    onFreshLeadFileChange (event) {
      const file = event?.target?.files?.[0] || null
      this.setFreshLeadFile(file)
      // Reset the input so re-selecting the same file fires change again.
      if (event && event.target) {
        try { event.target.value = '' } catch (e) { /* some browsers throw */ }
      }
    },
    // Validate + assign. Used by both the <input> change and the
    // drop handler so they go through the same gate.
    setFreshLeadFile (file) {
      this.freshLeads.error = ''
      this.freshLeads.lastSummary = ''
      if (!file) {
        this.freshLeads.file = null
        return
      }
      const name = String(file.name || '').toLowerCase()
      const ok = ['.xlsx', '.xls', '.csv'].some((ext) => name.endsWith(ext))
      if (!ok) {
        this.freshLeads.file = null
        this.freshLeads.error = 'Unsupported file type. Please pick a .xlsx, .xls, or .csv file.'
        return
      }
      this.freshLeads.file = file
    },
    onFreshLeadDragEnter () {
      if (this.freshLeads.uploading) return
      this.freshLeads.isDragOver = true
    },
    onFreshLeadDragOver () {
      if (this.freshLeads.uploading) return
      // dragover must be handled (and preventDefault'd via the @prevent
      // modifier in the template) for drop to fire — Vue swallows the
      // event for us, but we also flip the state to keep the highlight
      // alive while the pointer hovers.
      this.freshLeads.isDragOver = true
    },
    onFreshLeadDragLeave () {
      this.freshLeads.isDragOver = false
    },
    onFreshLeadDrop (event) {
      this.freshLeads.isDragOver = false
      if (this.freshLeads.uploading) return
      const file = event?.dataTransfer?.files?.[0] || null
      this.setFreshLeadFile(file)
    },
    formatFileSize (bytes) {
      const n = Number(bytes || 0)
      if (!Number.isFinite(n) || n <= 0) return ''
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
      return `${(n / (1024 * 1024)).toFixed(1)} MB`
    },
    previewFreshLeads () {
      if (!this.canPreviewFreshLeads) return
      this.freshLeads.uploading = true
      this.freshLeads.error = ''
      this.freshLeads.lastSummary = ''
      const form = new FormData()
      form.append('file', this.freshLeads.file)
      axios.post(
        `${this.$apiAdress}/api/call-queue/${this.activeOrgId}/fresh-leads/preview?token=${this.authToken()}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
        .then((response) => {
          const data = response.data || {}
          this.freshLeads.preview = {
            show: true,
            importId: data.import_id,
            fileName: data.original_filename || (this.freshLeads.file && this.freshLeads.file.name) || '',
            totalRows: data.total_rows || 0,
            headers: Array.isArray(data.headers) ? data.headers : [],
            sampleRows: Array.isArray(data.sample_rows) ? data.sample_rows : [],
            detectedMap: data.detected_map || {},
            // Copy the detected map into the editable one so the user can
            // override before confirming. Missing fields default to null
            // (= "Not mapped" in the dropdown).
            editableMap: this.buildEditableMap(data.canonical_fields || [], data.detected_map || {}),
            canonicalFields: Array.isArray(data.canonical_fields) ? data.canonical_fields : [],
            requiredFields: Array.isArray(data.required_fields) ? data.required_fields : [],
            confirmError: ''
          }
        })
        .catch((error) => {
          console.log(error)
          this.freshLeads.error = error?.response?.data?.message || 'Unable to parse the uploaded file.'
        })
        .finally(() => { this.freshLeads.uploading = false })
    },
    buildEditableMap (canonicalFields, detected) {
      const result = {}
      canonicalFields.forEach((field) => {
        result[field] = Object.prototype.hasOwnProperty.call(detected, field) ? detected[field] : null
      })
      return result
    },
    confirmFreshLeads () {
      if (!this.canConfirmFreshLeads) return
      const map = {}
      Object.entries(this.freshLeads.preview.editableMap).forEach(([field, idx]) => {
        if (idx !== null && idx !== undefined) {
          map[field] = idx
        }
      })
      const payload = {
        import_id: this.freshLeads.preview.importId,
        column_map: map
      }
      this.freshLeads.confirming = true
      this.freshLeads.preview.confirmError = ''
      axios.post(
        `${this.$apiAdress}/api/call-queue/${this.activeOrgId}/fresh-leads/confirm?token=${this.authToken()}`,
        payload
      )
        .then((response) => {
          this.freshLeads.lastSummary = response.data?.message || 'Imported.'
          this.$toast.success(this.freshLeads.lastSummary)
          this.closeFreshLeadsPreview()
          this.freshLeads.file = null
          // Refresh roster/pipeline counts so the Fresh leads tile picks
          // up the new row count.
          this.loadData()
        })
        .catch((error) => {
          console.log(error)
          this.freshLeads.preview.confirmError = error?.response?.data?.message || 'Import failed.'
        })
        .finally(() => { this.freshLeads.confirming = false })
    },
    closeFreshLeadsPreview () {
      this.freshLeads.preview.show = false
    },
    prettyFieldLabel (key) {
      const map = {
        name: 'Name',
        phone: 'Phone',
        address: 'Address',
        place: 'Place / village',
        district: 'District',
        state: 'State'
      }
      return map[key] || key
    },
    isRequiredField (key) {
      return (this.freshLeads.preview.requiredFields || []).includes(key)
    },

    isUserSelected (userId) {
      return this.selectedUserIdSet.has(userId)
    },
    togglePickerUser (user) {
      if (this.picker.submitting) return
      const idx = this.picker.selectedUsers.findIndex((u) => u.id === user.id)
      if (idx >= 0) {
        this.picker.selectedUsers.splice(idx, 1)
      } else {
        this.picker.selectedUsers.push({
          id: user.id, name: user.name, email: user.email, image: user.image
        })
      }
    },
    toggleSelectAllVisible () {
      if (this.picker.submitting) return
      const filtered = this.filteredPickerResults
      if (!filtered.length) return
      if (this.allFilteredSelected) {
        // Drop visible-and-selected. Selections that aren't currently
        // visible (filtered out by a search) stay put.
        const visibleIds = new Set(filtered.map((u) => u.id))
        this.picker.selectedUsers = this.picker.selectedUsers.filter((u) => !visibleIds.has(u.id))
      } else {
        const existing = this.selectedUserIdSet
        filtered.forEach((u) => {
          if (!existing.has(u.id)) {
            this.picker.selectedUsers.push({
              id: u.id, name: u.name, email: u.email, image: u.image
            })
          }
        })
      }
    },
    clearSelection () {
      if (this.picker.submitting) return
      this.picker.selectedUsers = []
    },
    async confirmBulkPicks () {
      if (!this.canConfirmBulk) return
      const target = this.picker.target
      const endpoint = target === 'resigned' ? 'resigned' : 'active'
      const queue = this.picker.selectedUsers.slice()
      this.picker.submitting = true
      this.picker.error = ''
      this.picker.submitProgress = { done: 0, total: queue.length }
      const failed = []
      for (let i = 0; i < queue.length; i += 1) {
        const user = queue[i]
        const payload = {
          organisation_id: this.activeOrgId,
          region_code: this.picker.regionCode,
          user_id: user.id
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          await axios.post(
            `${this.$apiAdress}/api/call-queue/${endpoint}?token=${this.authToken()}`,
            payload
          )
        } catch (error) {
          console.log(error)
          const data = error?.response?.data
          const fieldErrors = data?.errors ? Object.values(data.errors).flat() : []
          const msg = fieldErrors[0] || data?.message || 'request failed'
          failed.push({ user, msg })
        }
        this.picker.submitProgress = { done: i + 1, total: queue.length }
      }
      this.picker.submitting = false
      const succeeded = queue.length - failed.length

      if (!failed.length) {
        const label = succeeded === 1 ? `${queue[0].name} added.` : `${succeeded} users added.`
        this.$toast.success(label)
        this.closePicker()
        this.loadData()
        return
      }

      // Partial / total failure — keep the modal open with an inline error
      // so the admin can see what didn't go through.
      if (succeeded > 0) {
        this.$toast.success(`${succeeded} added.`)
      }
      const names = failed.slice(0, 3).map((f) => f.user.name).join(', ')
      const extra = failed.length > 3 ? `, +${failed.length - 3} more` : ''
      this.picker.error = `Failed to add ${failed.length} user${failed.length === 1 ? '' : 's'} (${names}${extra}). ${failed[0].msg}`
      // Drop succeeded users from the selection so a retry only re-runs failures.
      const failedIds = new Set(failed.map((f) => f.user.id))
      this.picker.selectedUsers = this.picker.selectedUsers.filter((u) => failedIds.has(u.id))
      // Refresh the roster + results so the now-added ones disappear from the list.
      this.loadData()
    },
    markResigned (region, user) {
      this.busyUserId = user.id
      axios.post(
        `${this.$apiAdress}/api/call-queue/resign?token=${this.authToken()}`,
        { organisation_id: this.activeOrgId, region_code: region.code, user_id: user.id }
      )
        .then((response) => {
          this.$toast.success(response.data?.message || `${user.name} marked resigned.`)
          this.loadData()
        })
        .catch((error) => {
          console.log(error)
          this.$toast.error(error?.response?.data?.message || 'Action failed.')
        })
        .finally(() => { this.busyUserId = null })
    },
    restoreActive (region, user) {
      this.busyUserId = user.id
      axios.post(
        `${this.$apiAdress}/api/call-queue/active?token=${this.authToken()}`,
        { organisation_id: this.activeOrgId, region_code: region.code, user_id: user.id }
      )
        .then((response) => {
          this.$toast.success(response.data?.message || `${user.name} restored as active.`)
          this.loadData()
        })
        .catch((error) => {
          console.log(error)
          this.$toast.error(error?.response?.data?.message || 'Action failed.')
        })
        .finally(() => { this.busyUserId = null })
    },
    removeMember (region, user, source) {
      this.busyUserId = user.id
      axios.post(
        `${this.$apiAdress}/api/call-queue/remove?token=${this.authToken()}`,
        { organisation_id: this.activeOrgId, region_code: region.code, user_id: user.id }
      )
        .then((response) => {
          this.$toast.success(response.data?.message || `${user.name || 'User'} removed from ${region.code}.`)
          this.loadData()
        })
        .catch((error) => {
          console.log(error)
          this.$toast.error(error?.response?.data?.message || 'Remove failed.')
        })
        .finally(() => { this.busyUserId = null })
      // `source` reserved for future undo-toast behaviour.
      void source
    },
    labelFor (key) {
      const found = this.pipelines.find((p) => p.key === key)
      return found ? found.label : key
    },
    pipelineRowCount (key) {
      const status = this.pipelineStatus && this.pipelineStatus[key]
      return status && status.row_count != null ? status.row_count : 0
    },
    pipelineLastRun (key) {
      const status = this.pipelineStatus && this.pipelineStatus[key]
      return status && status.last_run ? status.last_run : ''
    },
    statusClassFor (key) {
      const lastRun = this.pipelineLastRun(key)
      if (!lastRun) return 'never'
      const d = new Date(String(lastRun).replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return 'never'
      const ageHours = (Date.now() - d.getTime()) / (60 * 60 * 1000)
      if (ageHours < 24) return 'recent'
      if (ageHours < 72) return 'stale'
      return 'old'
    },
    formatNumber (value) {
      const n = Number(value || 0)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('en-IN')
    },
    statusLabelFor (key) {
      switch (this.statusClassFor(key)) {
        case 'recent': return 'Ran in the last 24 hours'
        case 'stale':  return 'Last run 1–3 days ago'
        case 'old':    return 'Last run more than 3 days ago'
        default:       return 'Never run'
      }
    },
    formatTime (isoString) {
      if (!isoString) return '—'
      const d = new Date(isoString.replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return isoString
      const now = new Date()
      const sameDay = d.toDateString() === now.toDateString()
      const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      if (sameDay) return `today, ${time}`
      const date = `${d.getDate()}/${d.getMonth() + 1}`
      return `${date}, ${time}`
    },
    initials (name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    avatarStyle (user) {
      if (user && user.image) return {}
      const text = String(user?.name || user?.email || user?.id || '?')
      let hash = 0
      for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) >>> 0
      }
      const [from, to] = AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
      return { background: `linear-gradient(135deg, ${from}, ${to})` }
    },
    onAvatarError (event, user) {
      // If the photo fails, fall back to initials by clearing the src.
      if (user) user.image = ''
      if (event?.target) event.target.style.display = 'none'
    },
    regionColor (code) {
      return REGION_PALETTE[code] || REGION_PALETTE.KERALA
    }
  }
}
</script>

<style scoped>
.cq-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.cq-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.cq-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex: 1 1 auto;
  min-width: 0;
}

.cq-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.cq-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.cq-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.cq-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.cq-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.cq-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cq-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.refresh-btn__icon {
  flex-shrink: 0;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.cq-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: cq-spin 0.9s linear infinite;
}

@keyframes cq-spin {
  to { transform: rotate(360deg); }
}

.cq-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Org tabs ── */
.cq-tabs {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.cq-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.85rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cq-tab:hover:not(:disabled) {
  background: #f5fbf8;
  border-color: #c7ebda;
  color: #0e5b46;
}

.cq-tab.is-active {
  background: #ecfdf5;
  border-color: #16a07a;
  color: #0e5b46;
}

.cq-tab.is-disabled,
.cq-tab:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cq-tab__badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #fef3c7;
  color: #92400e;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
}

/* ── Pipelines panel ── */
.cq-pipelines {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.cq-pipelines__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.cq-pipelines__title {
  flex: 1 1 auto;
  min-width: 0;
}

.cq-pipelines__head h2 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.cq-pipelines__head p {
  margin: 0.25rem 0 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.cq-pipelines__head strong {
  color: #1f2a44;
}

.cq-runall {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 32px;
  min-width: 110px;
  padding: 0 0.95rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: filter 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.cq-runall:hover:not(:disabled) {
  filter: brightness(1.06);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.28);
}

.cq-runall:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.cq-runall__icon {
  flex-shrink: 0;
}

.cq-runall__spinner {
  width: 13px;
  height: 13px;
}

.cq-pipelines__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

.cq-pipeline {
  position: relative;
  border: 1px solid #e5e9f2;
  border-left: 4px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.85rem 0.95rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.cq-pipeline:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  transform: translateY(-2px);
}

.cq-pipeline--recent { border-left-color: #16a07a; }
.cq-pipeline--stale  { border-left-color: #f59e0b; }
.cq-pipeline--old    { border-left-color: #dc2626; }
.cq-pipeline--never  { border-left-color: #cbd5e1; }

.cq-pipeline.is-running {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.16);
}

.cq-pipeline__name {
  font-size: 0.7rem;
  font-weight: 700;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cq-pipeline__metric {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  margin: 0.1rem 0 0.2rem;
}

.cq-pipeline__count {
  font-size: 1.55rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.025em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.cq-pipeline--never .cq-pipeline__count {
  color: #94a3b8;
}

.cq-pipeline__unit {
  font-size: 0.68rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.cq-pipeline__time {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 500;
}

.cq-pipeline__time--never {
  color: #b45309;
  font-weight: 600;
}

.cq-pipeline__time-icon {
  flex-shrink: 0;
  opacity: 0.85;
}

.cq-pipeline__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.85rem;
  margin-top: 0.25rem;
  border: 1px solid #d4ece1;
  border-radius: 7px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cq-pipeline__btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.cq-pipeline__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cq-pipeline__btn-icon {
  flex-shrink: 0;
}

.cq-pipeline__spinner {
  width: 12px;
  height: 12px;
}

/* ── Day thresholds ── */
.cq-days {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.cq-days__head {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.cq-days__head h2 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.cq-days__head p {
  margin: 0.25rem 0 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.cq-days__loading {
  padding: 1rem;
  font-size: 0.82rem;
  color: #6b7280;
}

.cq-day-list {
  list-style: none;
  margin: 0;
  padding: 0.4rem 0.5rem;
  display: flex;
  flex-direction: column;
}

.cq-day-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.cq-day-row + .cq-day-row {
  border-top: 1px dashed #eef1f7;
  border-radius: 0;
  margin-top: -1px;
}

.cq-day-row.is-dirty {
  background: #fffbeb;
  border-color: #fde68a;
}

.cq-day-row__body {
  flex: 1;
  min-width: 0;
}

.cq-day-row__title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.cq-day-row__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cq-day-row__chip {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #ecfdf5;
  color: #0e5b46;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
}

.cq-day-row__chip--dirty {
  background: #fef3c7;
  color: #92400e;
}

.cq-day-row__desc {
  margin: 0.2rem 0 0;
  font-size: 0.74rem;
  color: #4b5563;
  line-height: 1.4;
}

.cq-day-row__bounds {
  margin: 0.2rem 0 0;
  font-size: 0.7rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.cq-day-row__field {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.cq-day-row__inputwrap {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.cq-day-row__input {
  width: 90px;
  height: 34px;
  padding: 0 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #1f2a44;
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cq-day-row__input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.cq-day-row.is-dirty .cq-day-row__input {
  border-color: #f59e0b;
}

.cq-day-row__suffix {
  font-size: 0.78rem;
  color: #6b7280;
  font-weight: 500;
}

.cq-day-row__reset {
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.7rem;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

.cq-day-row__reset:hover:not(:disabled) {
  color: #0e5b46;
}

.cq-day-row__reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cq-days__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
}

.cq-days__hint {
  font-size: 0.74rem;
  color: #92400e;
  font-weight: 600;
}

.cq-days__save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 1rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(14, 91, 70, 0.18);
  transition: filter 0.15s ease, transform 0.15s ease;
}

.cq-days__save:hover:not(:disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.cq-days__save:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cq-days__spinner {
  width: 13px;
  height: 13px;
}

@media (max-width: 600px) {
  .cq-day-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  .cq-day-row__field {
    align-items: flex-start;
  }
}

/* ── Region cards ── */
.cq-region {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.cq-region__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cq-region__title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.cq-region__marker {
  width: 12px;
  height: 12px;
  border-radius: 4px;
}

.cq-region__title h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cq-region__chip {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #f1f5f9;
  color: #475569;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
}

.cq-region__counts {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  color: #1f2a44;
  font-weight: 600;
}

.cq-region__count--muted {
  color: #6b7280;
  font-weight: 500;
}

.cq-region__dot {
  color: #cbd5e1;
}

/* ── Roster ── */
.cq-roster {
  padding: 0.85rem 1.1rem;
}

.cq-roster + .cq-roster {
  border-top: 1px dashed #eef1f7;
}

.cq-roster__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
  gap: 0.5rem;
}

.cq-roster__head--inline {
  margin-bottom: 0.55rem;
}

.cq-roster h3 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.cq-roster__hint {
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 500;
  margin-left: 0.5rem;
}

.cq-roster__empty {
  font-size: 0.8rem;
  color: #6b7280;
  background: #f8fafc;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  border: 1px dashed #e5e9f2;
}

.cq-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 30px;
  padding: 0 0.85rem;
  border: 1px solid #d4ece1;
  border-radius: 7px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.cq-add-btn:hover {
  background: #f5fbf8;
  border-color: #16a07a;
}

.cq-add-btn--small {
  height: 26px;
  padding: 0 0.65rem;
  font-size: 0.74rem;
}

/* ── Resigned section (collapsible) ── */
.cq-roster--resigned {
  background: #fbfcfe;
}

.cq-roster--resigned[open] {
  background: #ffffff;
}

.cq-roster__summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  user-select: none;
  margin: -0.85rem -1.1rem;
  padding: 0.85rem 1.1rem;
}

.cq-roster__summary::-webkit-details-marker {
  display: none;
}

.cq-roster__summary::before {
  content: '▸';
  color: #6b7280;
  font-size: 0.7rem;
  transition: transform 0.15s ease;
}

.cq-roster--resigned[open] .cq-roster__summary::before {
  transform: rotate(90deg);
}

.cq-roster__body {
  padding-top: 0.85rem;
  margin-top: 0.85rem;
  border-top: 1px solid #eef1f7;
}

/* ── Member list ── */
.cq-member-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.5rem;
}

.cq-member {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.cq-member:hover {
  border-color: #c7ebda;
  box-shadow: 0 2px 8px rgba(14, 91, 70, 0.08);
  transform: translateY(-1px);
}

.cq-member-list--resigned .cq-member {
  background: #fafafa;
}

.cq-member__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  overflow: hidden;
  background: #cbd5e1;
}

.cq-member__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cq-member__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.cq-member__name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cq-member__email {
  font-size: 0.72rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cq-member__note {
  font-size: 0.7rem;
  color: #92400e;
  font-style: italic;
  margin-top: 0.15rem;
}

.cq-member__actions {
  display: flex;
  gap: 0.35rem;
  flex-shrink: 0;
  margin-left: auto;
}

.cq-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
}

.cq-icon-btn:hover:not(:disabled) {
  background: #f8fafc;
}

.cq-icon-btn:active:not(:disabled) {
  transform: scale(0.94);
}

.cq-icon-btn--warn {
  color: #b45309;
  border-color: #fde68a;
  background: #fffbeb;
}

.cq-icon-btn--warn:hover:not(:disabled) {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.cq-icon-btn--danger {
  color: #b91c1c;
  border-color: #fecaca;
  background: #fef2f2;
}

.cq-icon-btn--danger:hover:not(:disabled) {
  background: #fee2e2;
  border-color: #ef4444;
  color: #991b1b;
}

.cq-icon-btn--success {
  color: #0e5b46;
  border-color: #c7ebda;
  background: #f5fbf8;
}

.cq-icon-btn--success:hover:not(:disabled) {
  background: #ecfdf5;
  border-color: #16a07a;
}

.cq-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Empty fallback ── */
.cq-empty {
  background: #ffffff;
  border: 1px dashed #e5e9f2;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  text-align: center;
}

.cq-empty__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}

.cq-empty__hint {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.2rem;
}

/* ── Fresh-leads import (card + modal) ── */
.fli-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.fli-card__head {
  padding: 1rem 1.15rem 0.65rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.fli-card__title h2 {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.fli-card__title p {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
  max-width: 720px;
}

.fli-card__body {
  padding: 0.85rem 1.15rem 1.1rem;
}

.fli-controls {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.fli-dropzone {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.85rem;
  padding: 1.2rem 1rem;
  background: #fbfcfe;
  border: 2px dashed #d8dde6;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.fli-dropzone:hover { border-color: #16a07a; background: #f5fbf8; }

.fli-dropzone.is-dragover {
  border-color: #16a07a;
  background: #ecfdf5;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.12);
}

.fli-dropzone.is-filled {
  border-style: solid;
  border-color: #c7ebda;
  background: #ffffff;
}

.fli-dropzone.is-disabled { opacity: 0.55; cursor: not-allowed; }

.fli-dropzone input[type="file"] { display: none; }

.fli-dropzone__inner {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  text-align: left;
  min-width: 0;
}

.fli-dropzone__icon {
  flex-shrink: 0;
  color: #16a07a;
}

.fli-dropzone__text {
  font-size: 0.85rem;
  color: #1f2a44;
  min-width: 0;
}

.fli-dropzone__filename {
  display: block;
  font-weight: 700;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fli-dropzone__link { color: #0e5b46; font-weight: 700; }

.fli-dropzone__hint {
  margin-top: 0.15rem;
  font-size: 0.74rem;
  color: #6b7280;
  font-weight: 500;
}

.fli-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 1rem;
  border: 0;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.fli-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.fli-btn--primary:hover:not(:disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.fli-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.fli-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #c7ebda;
}

.fli-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  filter: saturate(0.5);
  transform: none;
}

.fli-btn:focus { outline: none; }
.fli-btn::-moz-focus-inner { border: 0; }
.fli-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.fli-btn__spinner { margin-right: 0.1rem; }

.fli-alert {
  margin-top: 0.7rem;
  padding: 0.55rem 0.7rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
}

.fli-summary {
  margin-top: 0.7rem;
  padding: 0.55rem 0.7rem;
  background: #ecfdf5;
  color: #0e5b46;
  border: 1px solid #c7ebda;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

/* ── Mapping modal ── */
.fli-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.fli-modal >>> .modal-header {
  background: #ffffff;
  border-bottom: 1px solid #eef1f7;
  padding: 0.95rem 1.15rem;
}

.fli-modal >>> .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
}

.fli-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.fli-modal >>> .modal-footer {
  background: #ffffff;
  border-top: 1px solid #eef1f7;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.fli-preview {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.fli-preview__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.fli-preview__file {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2a44;
  word-break: break-all;
}

.fli-preview__meta {
  font-size: 0.74rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.fli-mapping,
.fli-sample {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.fli-mapping__title,
.fli-sample__title {
  padding: 0.65rem 0.85rem;
  background: #fafbfd;
  border-bottom: 1px solid #eef1f7;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.fli-mapping__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem 0.85rem;
  padding: 0.75rem 0.85rem;
}

@media (max-width: 575px) {
  .fli-mapping__grid { grid-template-columns: 1fr; }
}

.fli-mapping__row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.fli-mapping__label {
  font-size: 0.74rem;
  font-weight: 600;
  color: #4f5560;
}

.fli-mapping__req { color: #b91c1c; margin-left: 0.15rem; }

.fli-mapping__select {
  height: 34px;
  padding: 0 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #1f2a44;
  font-family: inherit;
  cursor: pointer;
}

.fli-mapping__select:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.fli-sample__scroll {
  max-height: 320px;
  overflow: auto;
}

.fli-sample__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  color: #1f2a44;
}

.fli-sample__table th,
.fli-sample__table td {
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid #eef1f7;
  text-align: left;
  white-space: nowrap;
}

.fli-sample__table th {
  background: #fbfcfe;
  font-weight: 700;
  color: #4f5560;
  position: sticky;
  top: 0;
  z-index: 1;
}

.fli-sample__table tr:nth-child(even) td {
  background: #fafbfd;
}

/* ── Picker modal ── */
.cq-modal >>> .modal-content {
  border-radius: 12px;
  border: 1px solid #e5e9f2;
}

.cq-modal >>> .modal-header {
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
  padding: 0.85rem 1.1rem;
  border-radius: 12px 12px 0 0;
}

.cq-modal >>> .modal-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cq-modal >>> .modal-body {
  padding: 1rem 1.1rem;
}

.cq-modal >>> .modal-footer {
  background: #fbfcfe;
  border-top: 1px solid #eef1f7;
  padding: 0.7rem 1.1rem;
  border-radius: 0 0 12px 12px;
}

.cq-picker {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.cq-picker__alert {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.55rem 0.7rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.35;
}

.cq-picker__alert-text { flex: 1; min-width: 0; }

.cq-picker__alert-close {
  border: 0;
  background: transparent;
  color: #991b1b;
  font-size: 1.05rem;
  line-height: 1;
  padding: 0 0.15rem;
  cursor: pointer;
  flex-shrink: 0;
}

.cq-picker__alert-close:focus { outline: none; }
.cq-picker__alert-close::-moz-focus-inner { border: 0; }
.cq-picker__alert-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
  border-radius: 4px;
}

.cq-picker__searchwrap {
  position: relative;
}

.cq-picker__icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.cq-picker__input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.85rem 0.3rem 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cq-picker__input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.cq-picker__input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
  opacity: 0.7;
}

.cq-picker__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.cq-picker__selectall {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2a44;
  cursor: pointer;
  user-select: none;
}

.cq-picker__selectall input[type="checkbox"] {
  width: 15px;
  height: 15px;
  accent-color: #16a07a;
  cursor: pointer;
}

.cq-picker__selectall.is-disabled {
  color: #94a3b8;
  cursor: not-allowed;
}

.cq-picker__selectall.is-disabled input[type="checkbox"] {
  cursor: not-allowed;
}

.cq-picker__counter {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.76rem;
  font-weight: 600;
  color: #0e5b46;
  background: #ecfdf5;
  border: 1px solid #c7ebda;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.cq-picker__clear {
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

.cq-picker__clear:hover:not(:disabled) { color: #b91c1c; }
.cq-picker__clear:disabled { opacity: 0.55; cursor: not-allowed; }

.cq-picker__state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem;
  font-size: 0.82rem;
  color: #4b5563;
  background: #f8fafc;
  border-radius: 8px;
}

.cq-picker__state--empty {
  font-style: italic;
}

.cq-picker__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  max-height: 320px;
  overflow-y: auto;
}

.cq-picker__item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  margin: 0;
  padding: 0.5rem 0.65rem;
  border: 1px solid #e9edf3;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.cq-picker__item:hover:not(.is-disabled) {
  background: #f5fbf8;
  border-color: #c7ebda;
}

.cq-picker__item.is-selected {
  background: #ecfdf5;
  border-color: #16a07a;
  box-shadow: 0 0 0 1px rgba(22, 160, 122, 0.18);
}

.cq-picker__item.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cq-picker__check {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #16a07a;
  cursor: pointer;
  flex-shrink: 0;
}

.cq-picker__item.is-disabled .cq-picker__check { cursor: not-allowed; }

.cq-picker__submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 34px;
  min-width: 130px;
  padding: 0 1rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: filter 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.cq-picker__submit:hover:not(:disabled) {
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.cq-picker__submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  filter: saturate(0.5);
  transform: none;
  box-shadow: none;
}

.cq-picker__submit:focus { outline: none; }
.cq-picker__submit::-moz-focus-inner { border: 0; }
.cq-picker__submit:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.cq-picker__submit-spinner { width: 13px; height: 13px; }

.cancel-btn {
  height: 34px;
  padding: 0 1rem;
  border-radius: 7px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.cancel-btn:hover {
  background: #f8fafc;
}

@media (max-width: 767px) {
  .cq-toolbar {
    flex-wrap: wrap;
  }
  .cq-pipelines__head {
    flex-wrap: wrap;
  }
  .cq-runall {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .cq-page {
    padding: 0 0 0.5rem;
  }
  .cq-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .cq-toolbar__accent {
    height: 30px;
  }
  .cq-toolbar__title h1 {
    font-size: 1rem;
  }
  .cq-toolbar__title p {
    font-size: 0.72rem;
  }
  .cq-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .cq-tabs {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .cq-region__head {
    padding: 0.7rem 0.85rem;
  }
  .cq-modal >>> .modal-content {
    border-radius: 12px;
  }
  .cq-modal >>> .modal-body {
    padding: 0.85rem;
  }
}
</style>
